/**
 * Side panel of the graph lights view: the diagnosis status, a node search, the top causes (hover highlights, click selects),
 * the legend; or, with a node selected, its details (NodeDetails).
 */
import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";

import { SEARCH_MAX_RESULTS, TONES } from "./lightsConstants";
import { isCandidate, isNodeShown } from "./lightsState";
import { LIGHTS_TEXT, STAGE_WORD, formatPercent } from "./lightsText";
import NodeDetails from "./NodeDetails";

const ICON_PX = 14;
const list = (v) => (Array.isArray(v) ? v : []);
const normalise = (t) => String(t || "").toLowerCase().replace(/\s+/g, " ").trim();

function StatusCard({ lights, diagState }) {
  const { summary, stage } = lights;
  const lead = summary.leading[0];
  const lods = list(diagState?.lods).filter((l) => l && l.id);
  return (
    <section className="d5-lights-card" aria-label={LIGHTS_TEXT.status}>
      <h4 className="d5-lights-label">{LIGHTS_TEXT.status}</h4>
      <div className="d5-lights-stage-word">{STAGE_WORD[stage] || stage}{diagState?.phase ? <span className="d5-lights-muted"> · {diagState.phase}</span> : null}</div>
      {lead && <div className="d5-lights-lead"><span>{lead.label}</span><b>{formatPercent(lead.pct)}</b></div>}
      <dl className="d5-lights-stats">
        <div><dt>Causes</dt><dd>{summary.causes}</dd></div>
        <div><dt>Lit</dt><dd>{summary.lit}</dd></div>
        <div><dt>Ruled out</dt><dd>{summary.ruledOut}</dd></div>
        <div><dt>Seen</dt><dd>{summary.observed}</dd></div>
      </dl>
      {lods.length > 0 && (
        <ul className="d5-lights-lods" aria-label={LIGHTS_TEXT.lines}>
          {lods.map((l) => (
            <li key={l.id}>
              <span className="d5-lights-lod-label">{l.label || l.id}</span>
              <span className="d5-lights-bar" aria-hidden="true"><span style={{ width: `${Math.round((lights.clusterMass.get(l.id) || 0) * 100)}%` }} /></span>
              <span className="d5-lights-pct">{formatPercent((lights.clusterMass.get(l.id) || 0) * 100)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SearchBox({ lights, layout, options, onSelect, onHover }) {
  const [query, setQuery] = useState("");
  const results = useMemo(() => {
    const q = normalise(query);
    if (!q) return [];
    const hits = [];
    lights.nodes.forEach((n) => {
      if (!layout.positions.has(n.nid) || !isNodeShown(n, options)) return;
      if (normalise(n.label).includes(q) || normalise(n.nid).includes(q)) hits.push(n);
    });
    const rank = (n) => (n.tone === TONES.LEADING ? 3 : isCandidate(n) ? 2 : n.observed ? 1 : 0);
    return hits.sort((a, b) => (rank(b) - rank(a)) || a.label.localeCompare(b.label)).slice(0, SEARCH_MAX_RESULTS);
  }, [query, lights, layout, options]);
  const pick = (nid) => { onSelect({ node: nid }); setQuery(""); onHover(null); };
  const onKeyDown = (e) => { if (e.key === "Enter" && results[0]) { e.preventDefault(); pick(results[0].nid); } };
  return (
    <section className="d5-lights-card">
      <label className="d5-lights-search">
        <Search size={ICON_PX} aria-hidden="true" />
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown}
          placeholder={LIGHTS_TEXT.searchPlaceholder} aria-label={LIGHTS_TEXT.search} />
      </label>
      {query && (
        results.length ? (
          <ul className="d5-lights-results">
            {results.map((n) => (
              <li key={n.nid}>
                <button type="button" className="d5-lights-row" onClick={() => pick(n.nid)}
                  onMouseEnter={() => onHover(n.nid)} onMouseLeave={() => onHover(null)} onFocus={() => onHover(n.nid)} onBlur={() => onHover(null)}>
                  <span className={`d5-lights-dot d5-lights-dot--${n.role} d5-lights-dot--${n.tone}`} aria-hidden="true" />
                  <span className="d5-lights-row-label">{n.label}</span>
                  <span className="d5-lights-muted">{LIGHTS_TEXT.roles[n.role]}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="d5-lights-muted">{LIGHTS_TEXT.noMatch}</p>
      )}
    </section>
  );
}

function TopCauses({ lights, onSelect, onHover }) {
  const { summary } = lights;
  const row = (c, pct) => (
    <li key={c.nid}>
      <button type="button" className="d5-lights-row" onClick={() => onSelect({ node: c.nid })}
        onMouseEnter={() => onHover(c.nid)} onMouseLeave={() => onHover(null)} onFocus={() => onHover(c.nid)} onBlur={() => onHover(null)}>
        <span className="d5-lights-row-label">{c.label}</span>
        {pct != null && <span className="d5-lights-pct">{formatPercent(pct)}</span>}
      </button>
      {pct != null && <span className="d5-lights-bar" aria-hidden="true"><span style={{ width: `${Math.round(pct)}%` }} /></span>}
    </li>
  );
  return (
    <section className="d5-lights-card">
      <h4 className="d5-lights-label">{LIGHTS_TEXT.topCauses}</h4>
      {summary.leading.length ? <ol className="d5-lights-causes">{summary.leading.map((c) => row(c, c.pct))}</ol> : <p className="d5-lights-muted">{LIGHTS_TEXT.noCauses}</p>}
      {summary.ruled.length > 0 && (
        <details className="d5-lights-fold">
          <summary>{LIGHTS_TEXT.ruledOut}<span className="d5-lights-count">{summary.ruled.length}</span></summary>
          <ul className="d5-lights-causes d5-lights-causes--ruled">{summary.ruled.map((c) => row(c, null))}</ul>
        </details>
      )}
    </section>
  );
}

export function Legend() {
  return (
    <section className="d5-lights-card d5-lights-legend-card">
      <h4 className="d5-lights-label">{LIGHTS_TEXT.legendLabel}</h4>
      <ul className="d5-lights-legend" aria-label={LIGHTS_TEXT.legendLabel}>
        {LIGHTS_TEXT.legend.map(({ role, tone, label }) => (
          <li key={`${role}-${tone}`}><span className={`d5-lights-dot d5-lights-dot--${role} d5-lights-dot--${tone}`} aria-hidden="true" />{label}</li>
        ))}
        {LIGHTS_TEXT.legendEdges.map(({ kind, label }) => (
          <li key={kind}><span className={`d5-lights-edge-swatch d5-lights-edge-swatch--${kind}`} aria-hidden="true" />{label}</li>
        ))}
      </ul>
    </section>
  );
}

/** `selection` = {node: nid} | {edge: id} | null; `onSelect(selection)`; `onHover(nid | null)` highlights a node on the canvas. */
export default function LightsSidebar({ lights, layout, diagState, bundle, options, selection, learnedKey, onSelect, onHover }) {
  if (selection) {
    return (
      <NodeDetails selection={selection} lights={lights} layout={layout} diagState={diagState} bundle={bundle} learnedKey={learnedKey}
        onSelect={onSelect} onBack={() => onSelect(null)} />
    );
  }
  return (
    <div className="d5-lights-panel-body">
      <StatusCard lights={lights} diagState={diagState} />
      <SearchBox lights={lights} layout={layout} options={options} onSelect={onSelect} onHover={onHover} />
      <TopCauses lights={lights} onSelect={onSelect} onHover={onHover} />
      <Legend />
    </div>
  );
}
